import { saveAs } from 'file-saver';

import { Campaign } from '../types/campaign.types';
import { GetConnectivityResponse, ImportFieldsFormat } from '../types/connectivity';
import {
  SubmitImportSubmissionParams,
  SubmitImportSubmissionResponse,
} from '../types/import-submission';
import { FORM_STATUS } from '../types/form-status';
import { handleRequest } from '../utils/handle-request';

import { privateClient } from './api-client';

export interface ClauseCampaignOption {
  value: string;
  label: string;
}

export const sharedApi = {
  campaigns: async () => {
    const campaigns = await privateClient.get('campaigns/').json<Campaign[]>();

    return campaigns.sort((a, b) => a.year - b.year);
  },
  clauseCampaigns: async () => {
    return privateClient
      .get('clause_campaigns/')
      .json<ClauseCampaignOption[]>();
  },
  cgu: async () => {
    const cgu = await privateClient.get('accounts/tutorial/').json<{
      file: string;
    }>();

    return cgu;
  },
  uploadYieldMap: ({ file, farm, yield_float }: UploadYieldMapParams) => {
    const data = new FormData();
    data.append('file', file);
    data.append('farm', farm.toString());
    data.append('yield_float', yield_float.toString());

    return privateClient
      .post('upload_yield_map/', {
        body: data,
      })
      .json<{ status: string }>();
  },
  getComputationStatus: async (requestId: number) => {
    return privateClient.get(`computation_requests/${requestId}/`).json<{
      id: number;
      status: FORM_STATUS;
      instance_id: number | null;
      errors?: string | null;
      warnings?: string | null;
    }>();
  },

  getConnectivity: async () => {
    return privateClient.get('connectivity/').json<GetConnectivityResponse>();
  },
  importConnectivity: async (params: {
    data_file: File;
    fields_file: File;
    format: ImportFieldsFormat;
    id: number;
  }) => {
    const formData = new FormData();
    formData.append('data_file', params.data_file);
    formData.append('fields_file', params.fields_file);
    formData.append('format', params.format);
    formData.append('action', 'import');

    const res = await handleRequest<{
      status: string;
      async_computation_request_id: number;
    }>(
      privateClient
        .post(`connectivity/${params.id}/`, {
          body: formData,
        })
        .json(),
    );

    return res;
  },
  submitImportSubmission: async (params: SubmitImportSubmissionParams) => {
    const formData = new FormData();
    formData.append('clause', params.clause);
    formData.append('volume_type', params.volume_type);
    if (params.campaign_year !== undefined) {
      formData.append('campaign_year', String(params.campaign_year));
    }
    formData.append('data_file', params.data_file);
    if (params.fields_file) {
      formData.append('fields_file', params.fields_file);
    }

    return handleRequest<SubmitImportSubmissionResponse>(
      privateClient
        .post('import-submissions/', {
          body: formData,
        })
        .json(),
    );
  },
  submitImportForReview: async (params: {
    data_file: File;
    fields_file: File;
    format: ImportFieldsFormat;
    id: number;
  }) => {
    const formData = new FormData();
    formData.append('data_file', params.data_file);
    formData.append('fields_file', params.fields_file);
    formData.append('format', params.format);
    formData.append('action', 'submit_for_review');

    return handleRequest<{
      status: string;
      mass_import_id: number;
    }>(
      privateClient
        .post(`connectivity/${params.id}/`, {
          body: formData,
        })
        .json(),
    );
  },

  exportConnectivity: async (params: {
    id: number;
  }) => {
    const formData = new FormData();
    formData.append('action', 'export');

    const res = await handleRequest(
      privateClient.post(`connectivity/${params.id}/`, {
        body: formData,
      }),
    );

    // Extract filename from header
    const filename =
      res.headers
        .get('content-disposition')
        ?.split(';')
        .find((n) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim() || `export_${params.id}.zip`;

    const blob = await res.blob();

    saveAs(blob, filename);

    return res;
  },
};

interface UploadYieldMapParams {
  file: File;
  farm: number;
  yield_float: number;
  comment: string;
}
